import React, { useState, useEffect, useRef } from 'react';
import { Settings, Menu, X, MessageSquare, Server, Wrench, Wifi, WifiOff, AlertCircle, RefreshCw } from 'lucide-react';
import ChatInterface from './components/ChatInterface';
import ServerPanel from './components/ServerPanel';
import ToolPanel from './components/ToolPanel';
import SettingsPanel from './components/SettingsPanel';
import { useHTTPStream } from './hooks/useHTTPStream';
import { useMCPAPI } from './hooks/useMCPAPI';
import './index.css'

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activePanel, setActivePanel] = useState('chat');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'system',
      content: 'MCP Playground initializing...',
      timestamp: new Date().toLocaleTimeString()
    }
  ]);

  // Add refs to track message states and prevent duplicates
  const lastConnectionState = useRef(null);
  const hasShownConnectionError = useRef(false);
  const hasShownStreamError = useRef(false);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  // HTTP Stream for real-time updates
  const { connectionStatus, connect, disconnect } = useHTTPStream(apiUrl, {
    onMessage: handleStreamMessage,
    onError: (error) => {
      // Only show stream error once
      if (!hasShownStreamError.current) {
        addMessage('Stream connection failed. Real-time updates disabled.', 'error');
        hasShownStreamError.current = true;
      }
    },
    onOpen: () => {
      addMessage('Connected to real-time updates.', 'system');
      hasShownStreamError.current = false; // Reset error flag on successful connection
    }
  });

  // API for server operations with auto-connection
  const {
    servers,
    tools,
    resources,
    connected,
    loading,
    autoConnecting,
    listTools,
    callTool,
    listResources,
    getResource,
    retryConnection,
    setTools,
    setResources,
  } = useMCPAPI(apiUrl);

  function handleStreamMessage(data: any) {
    console.log('Received stream message:', data);
    
    switch (data.type) {
      case 'connected':
        addMessage(data.message, 'system');
        break;
      case 'server_connected':
        addMessage(`Connected to MCP server: ${data.data?.name || 'Unknown'}`, 'system');
        break;
      case 'server_disconnected':
        addMessage(`Disconnected from server: ${data.serverName}`, 'system');
        break;
      case 'tool_call_start':
        addMessage(`Calling tool: ${data.data.toolName}...`, 'system');
        break;
      case 'tool_result':
        const result = data.data?.result || data.data;
        addMessage(`Tool result: ${JSON.stringify(result, null, 2)}`, 'assistant');
        break;
      case 'tool_error':
        addMessage(`Tool error: ${data.data.error}`, 'error');
        break;
      case 'resource_fetch_start':
        addMessage(`Fetching resource: ${data.data.uri}...`, 'system');
        break;
      case 'resource_content':
        const content = data.data?.resource || data.data;
        addMessage(`Resource content: ${JSON.stringify(content, null, 2)}`, 'assistant');
        break;
      case 'resource_error':
        addMessage(`Resource error: ${data.data.error}`, 'error');
        break;
      case 'error':
        addMessage(`Error: ${data.message}`, 'error');
        break;
      case 'server_shutdown':
        addMessage('Server is shutting down...', 'system');
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  function addMessage(content: string, type = 'user') {
    const newMessage = {
      id: Date.now(),
      type,
      content,
      timestamp: new Date().toLocaleTimeString()
    };
    setMessages(prev => [...prev, newMessage]);
  }

  // Handle auto-connection status
  useEffect(() => {
    if (autoConnecting) {
      addMessage('Connecting to MCP server with your API key...', 'system');
    } else if (connected) {
      addMessage(`✅ Connected to MCP server successfully!`, 'system');
    } else {
      addMessage('❌ Failed to connect to MCP server. You may need to check your API key or try again.', 'error');
    }
  }, [autoConnecting, connected]); // Remove tools.length, resources.length dependencies

  // Auto-connect to stream (only once)
  useEffect(() => {
    connect();
  }, []); // Empty dependency array to run only once

  const handleCallTool = async (toolName: string, args: any = {}) => {
    try {
      if (servers.length === 0) {
        addMessage('No servers connected', 'error');
        return;
      }
      
      const serverName = servers[0].name || 'plusefin-mcp';
      await callTool(serverName, toolName, args);
      // Result will be handled by stream message
    } catch (error) {
      addMessage(`Failed to call tool: ${error.message}`, 'error');
    }
  };

  const handleGetResource = async (uri: string) => {
    try {
      if (servers.length === 0) {
        addMessage('No servers connected', 'error');
        return;
      }
      
      const serverName = servers[0].name || 'plusefin-mcp';
      await getResource(serverName, uri);
      // Result will be handled by stream message
    } catch (error) {
      addMessage(`Failed to get resource: ${error.message}`, 'error');
    }
  };

  const handleRetryConnection = async () => {
    // Reset error flags before retrying
    hasShownConnectionError.current = false;
    hasShownStreamError.current = false;
    
    addMessage('Retrying connection...', 'system');
    try {
      await retryConnection();
      // Also retry stream connection
      disconnect();
      setTimeout(() => connect(), 1000);
    } catch (error) {
      addMessage(`Retry failed: ${error.message}`, 'error');
    }
  };

  const getConnectionIcon = () => {
    if (autoConnecting || loading) {
      return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
    }
    
    if (connected) {
      return <Wifi className="w-4 h-4 text-green-500" />;
    }
    
    return <WifiOff className="w-4 h-4 text-red-500" />;
  };

  const getConnectionStatus = () => {
    if (autoConnecting) return 'Connecting...';
    if (loading) return 'Loading...';
    if (connected) return 'Connected';
    return 'Disconnected';
  };

  const panels = [
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'servers', label: 'Servers', icon: Server },
    { id: 'tools', label: 'Tools', icon: Wrench },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <h1 className="text-xl font-bold text-gray-800">MCP Playground</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="flex-1 px-4 py-4 space-y-2">
          {panels.map((panel) => {
            const Icon = panel.icon;
            return (
              <button
                key={panel.id}
                onClick={() => {
                  setActivePanel(panel.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors ${
                  activePanel === panel.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                {panel.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b h-16 flex items-center justify-between px-4 relative z-10">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 mr-2"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-800 capitalize">
              {activePanel}
            </h2>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {getConnectionIcon()}
              <span className="text-sm text-gray-600">{getConnectionStatus()}</span>
            </div>
            
            {!connected && !autoConnecting && (
              <button
                onClick={handleRetryConnection}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                disabled={loading}
              >
                Retry
              </button>
            )}
          </div>
        </header>

        {/* Panel content */}
        <div className="flex-1 overflow-hidden">
          {activePanel === 'chat' && (
            <ChatInterface 
              messages={messages} 
              onSendMessage={addMessage}
              onCallTool={handleCallTool}
              tools={tools}
              connected={connected}
            />
          )}
          {activePanel === 'servers' && (
            <ServerPanel 
              servers={servers}
              connected={connected}
              loading={loading || autoConnecting}
              tools={tools}
              resources={resources}
              onRetry={handleRetryConnection}
            />
          )}
          {activePanel === 'tools' && (
            <ToolPanel 
              tools={tools}
              onCallTool={handleCallTool}
              connected={connected}
            />
          )}
          {activePanel === 'settings' && (
            <SettingsPanel />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
